import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RefreshCw, Download, Send, AlertTriangle } from "lucide-react";
import { api, apiError, API, fmtDate } from "../../lib/api";
import { localeOf } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";

const TONE = {
  pending: "bg-muted text-muted-foreground",
  sent: "bg-primary text-primary-foreground",
  confirmed: "bg-[hsl(var(--success))] text-white",
  retry: "bg-[hsl(var(--warning))] text-white",
  failed: "bg-destructive text-destructive-foreground",
};

export default function LeadOutbox() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  const load = (status = filter) =>
    api.get(`/lead-outbox${status ? `?status=${status}` : ""}`).then(({ data }) => setData(data));
  useEffect(() => { load(filter).catch((e) => toast.error(apiError(e))); }, [filter]);

  const flush = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/lead-outbox/flush");
      toast.success(data.configured ? t("outbox.flushed", { n: data.processed }) : t("outbox.notConfigured"));
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const retry = async (id) => {
    try { await api.post(`/lead-outbox/${id}/retry`); toast.success(t("outbox.retried")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const counts = data?.counts || {};

  return (
    <div className="space-y-5" data-testid="lead-outbox-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold">{t("outbox.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("outbox.lead")}</p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button data-testid="outbox-flush-btn" onClick={flush} disabled={busy}
              className="h-11 rounded-full font-bold">
              <Send className="mr-2 h-4 w-4" />{t("outbox.flush")}
            </Button>
            <Button variant="outline" data-testid="outbox-export-btn"
              onClick={() => window.open(`${API}/lead-outbox/export`, "_blank")}
              className="h-11 rounded-full font-bold">
              <Download className="mr-2 h-4 w-4" />{t("outbox.export")}
            </Button>
          </div>
        )}
      </div>

      {data && !data.configured && (
        <div className="flex items-start gap-3 rounded-2xl bg-[hsl(var(--warning))]/15 p-4"
          data-testid="outbox-not-configured">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-[hsl(var(--warning))]" />
          <p className="text-sm">{t("outbox.notConfiguredHint")}</p>
        </div>
      )}
      {data?.configured && data.blocked && (
        <div className="flex items-start gap-3 rounded-2xl bg-destructive/15 p-4"
          data-testid="outbox-blocked">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-destructive" />
          <p className="text-sm">{t("outbox.blocked", { reason: data.blocked })}</p>
        </div>
      )}
      {data?.environment && (
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground"
          data-testid="outbox-environment">
          {t("outbox.environment")}: {data.environment}
        </p>
      )}

      <div className="flex flex-wrap gap-2" data-testid="outbox-status-filters">
        {["", "pending", "sent", "confirmed", "retry", "failed"].map((s) => (
          <button key={s || "all"} data-testid={`outbox-filter-${s || "all"}`} onClick={() => setFilter(s)}
            className={`h-11 rounded-full px-5 text-sm font-bold transition-colors ${
              filter === s ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`}>
            {s ? t(`outbox.status.${s}`) : t("analytics.all")}
            {s ? ` · ${counts[s] ?? 0}` : ""}
          </button>
        ))}
      </div>

      <section className="vy-card overflow-x-auto p-5">
        <table className="w-full min-w-[860px] text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="py-2">{t("outbox.contact")}</th>
              <th className="py-2">{t("outbox.attribution")}</th>
              <th className="py-2">{t("outbox.delivery")}</th>
              <th className="py-2">{t("outbox.externalId")}</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {(data?.deliveries || []).map((d) => (
              <tr key={d.id} className="border-t border-border" data-testid={`outbox-row-${d.id}`}>
                <td className="py-3 pr-4">
                  <p className="font-semibold">{d.contact_name || "—"}</p>
                  <p className="text-xs text-muted-foreground">
                    {[d.business_name, d.email, d.phone].filter(Boolean).join(" · ") || "—"}
                  </p>
                  {d.interest && <p className="text-xs text-muted-foreground">{d.interest}</p>}
                </td>
                <td className="py-3 pr-4 text-xs text-muted-foreground">
                  <p className="font-semibold text-foreground">{d.ambassador_name || "—"}</p>
                  <p>{[d.event_title, d.partner_name, d.campaign_name].filter(Boolean).join(" · ") || "—"}</p>
                  <p>{d.capture_mode} · {fmtDate(d.captured_at, locale)}</p>
                </td>
                <td className="py-3">
                  <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold uppercase ${TONE[d.status] || "bg-muted"}`}
                    data-testid={`outbox-status-${d.id}`}>
                    {t(`outbox.status.${d.status}`, d.status)}
                  </span>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("outbox.attemptsTotal")}: {d.sync_attempt_count ?? 0}
                    {d.manual_retry_count ? ` (${d.manual_retry_count} ${t("outbox.manual")})` : ""}
                    {d.last_failure_reason ? ` · ${d.last_failure_reason}` : ""}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {d.confirmed_at ? fmtDate(d.confirmed_at, locale)
                      : (d.last_sync_at ? fmtDate(d.last_sync_at, locale) : "—")}
                    {d.duplicate_idempotent ? " · idempotent" : ""}
                  </p>
                </td>
                <td className="py-3 text-xs text-muted-foreground">{d.vytalileads_lead_id || "—"}</td>
                <td className="py-3 text-right">
                  {isAdmin && d.status !== "confirmed" && (
                    <Button variant="outline" data-testid={`outbox-retry-${d.id}`} onClick={() => retry(d.id)}
                      className="h-10 rounded-full text-xs font-bold">
                      <RefreshCw className="mr-1.5 h-3.5 w-3.5" />{t("outbox.retry")}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {(data?.deliveries || []).length === 0 && (
              <tr><td colSpan={5} className="py-4 text-sm text-muted-foreground">{t("common.none")}</td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
